import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { LanguageSchema } from "./column-schemas.js";
import { CalendarDateFromString } from "./temporal.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { CreateRiskTemplatePayload } from "./admin-risk-templates.js";
import { InvalidRecurrenceConfig, MeasureTemplateCategory, MeasureTemplateRecurrence, MeasureTemplateDueDateInterval, MeasureTemplatePriority, } from "./admin-measure-templates.js";
import { QuestionnaireType } from "./admin-questionnaires.js";
import { RiskFactorNotFound } from "./admin-risk-catalog-values.js";
const GroupId = S.Int.pipe(S.positive());
const Text = S.Struct({
    language: LanguageSchema,
    title: S.String.pipe(S.filter((value) => value.trim().length > 0)),
    description: S.optional(S.NullOr(S.String)),
});
const uniqueLanguages = (variants) => new Set(variants.map((variant) => variant.language)).size === variants.length;
export const SaveCatalogueQuestionPayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(Text).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
    typeGroupId: S.NullOr(GroupId),
    industryGroupId: S.NullOr(GroupId),
    tags: S.Array(GroupId),
    risks: S.Array(GroupId),
});
export const SaveCatalogueQuestionnairePayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(S.extend(Text, S.Struct({
        reason: S.optional(S.NullOr(S.String)),
        inspection_report_title: S.optional(S.NullOr(S.String)),
    }))).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
    type: QuestionnaireType,
    assignAll: S.Boolean,
    clientIds: S.Array(S.UUID),
    tags: S.Array(GroupId),
    questions: S.Array(GroupId),
});
const Saved = S.Struct({
    groupId: GroupId,
    variants: S.Array(S.Struct({ id: S.UUID, language: LanguageSchema })),
});
export const SaveCatalogueRiskTemplatePayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(S.Struct({
        language: LanguageSchema,
        title: Text.fields.title,
        description: S.String,
    })).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
    properties: S.extend(CreateRiskTemplatePayload.omit("language", "title", "description", "translation_group_id", "status", "source_translation_group_id", "mode_translation_group_id"), S.Struct({
        source_translation_group_id: S.optional(S.NullOr(GroupId)),
        mode_translation_group_id: S.optional(S.NullOr(GroupId)),
    })),
    tags: S.Array(GroupId),
    laws: S.Array(GroupId),
    addedAttachments: S.Array(S.UUID),
});
export const SaveCatalogueMeasureTemplatePayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(S.extend(Text, S.Struct({ addedAttachments: S.Array(S.UUID) }))).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
    properties: S.Struct({
        priority: MeasureTemplatePriority,
        category: MeasureTemplateCategory,
        recurring: S.Boolean,
        recurrence: S.optional(S.NullOr(MeasureTemplateRecurrence)),
        due_date_interval: S.optional(S.NullOr(MeasureTemplateDueDateInterval)),
        due_date: S.optional(S.NullOr(CalendarDateFromString)),
        auto_reminder: S.Boolean,
        auto_assign_new_employees: S.Boolean,
    }),
});
export const SaveCatalogueValuePayload = S.Struct({
    collection: S.Literal("risk_template_factors", "risk_template_areas", "risk_template_objects", "risk_template_activities", "risk_template_sources", "risk_template_modes", "risk_template_tags", "questionnaire_tags", "questionnaire_types", "questionnaire_industries"),
    groupId: S.optional(GroupId),
    variants: S.Array(S.Struct({ language: LanguageSchema, value: Text.fields.title })).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
});
export const SaveCatalogueLawPayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(S.Struct({
        language: LanguageSchema,
        title: Text.fields.title,
        description: S.NonEmptyString,
        external_link: S.optional(S.NullOr(S.String)),
    })).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
});
export const SaveCatalogueSubfactorPayload = S.Struct({
    groupId: S.optional(GroupId),
    variants: S.Array(S.Struct({
        language: LanguageSchema,
        value: Text.fields.title,
        factor_id: GroupId,
    })).pipe(S.minItems(1), S.maxItems(2), S.filter(uniqueLanguages)),
});
const SavedValue = S.Struct({
    groupId: GroupId,
    variants: S.Array(S.Struct({ id: S.Int, language: LanguageSchema })),
});
/** A group id already claimed by a different catalogue collection. */
export const CatalogueGroupCollectionMismatch = S.Struct({
    _tag: S.Literal("CatalogueGroupCollectionMismatch"),
});
/** A referenced tag, law, type, industry, question or file no longer exists. */
export const CatalogueReferenceNotFound = S.Struct({
    _tag: S.Literal("CatalogueReferenceNotFound"),
});
export const catalogueAuthoringGroup = HttpApiGroup.make("catalogue-authoring")
    .add(HttpApiEndpoint.post("saveLaw", "/admin/catalogue/laws/save")
    .setPayload(SaveCatalogueLawPayload)
    .addSuccess(SavedValue)
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveSubfactor", "/admin/catalogue/subfactors/save")
    .setPayload(SaveCatalogueSubfactorPayload)
    .addSuccess(SavedValue)
    .addError(RiskFactorNotFound, { status: 404 })
    .addError(CatalogueReferenceNotFound, { status: 404 })
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveRiskTemplate", "/admin/catalogue/risk-templates/save")
    .setPayload(SaveCatalogueRiskTemplatePayload)
    .addSuccess(Saved)
    .addError(CatalogueReferenceNotFound, { status: 404 })
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveMeasureTemplate", "/admin/catalogue/measure-templates/save")
    .setPayload(SaveCatalogueMeasureTemplatePayload)
    .addSuccess(Saved)
    .addError(InvalidRecurrenceConfig, { status: 400 })
    .addError(CatalogueReferenceNotFound, { status: 404 })
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveValue", "/admin/catalogue/values/save")
    .setPayload(SaveCatalogueValuePayload)
    .addSuccess(S.Struct({
    groupId: GroupId,
    variants: S.Array(S.Struct({ id: S.Int, language: LanguageSchema })),
}))
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveQuestion", "/admin/catalogue/questions/save")
    .setPayload(SaveCatalogueQuestionPayload)
    .addSuccess(Saved)
    .addError(CatalogueReferenceNotFound, { status: 404 })
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("saveQuestionnaire", "/admin/catalogue/questionnaires/save")
    .setPayload(SaveCatalogueQuestionnairePayload)
    .addSuccess(Saved)
    .addError(CatalogueReferenceNotFound, { status: 404 })
    .addError(CatalogueGroupCollectionMismatch, { status: 409 })
    .addError(Forbidden, { status: 403 }))
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=catalogue-authoring.js.map